import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Stack,
  Accordion,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { AuditLogEntry } from '../../../core/types';
import {
  formatTimestamp,
  formatEntityName,
  parseChangeItems,
  getActionVerb,
} from '../utils/auditUtility';
import fieldDisplayNames from '../utils/fieldDisplayNames';

export interface ChangeDetailsDrawerProps {
  open: boolean;
  row: AuditLogEntry | null;
  onClose: () => void;
}

const ChangeDetailsDrawer: React.FC<ChangeDetailsDrawerProps> = ({
  open,
  row,
  onClose,
}) => {
  const theme = useTheme();
  const items = parseChangeItems((row as any)?.changeJson);
  // eslint-disable-next-line no-debugger
  // debugger;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: 340, sm: 420 },
          p: 2,
          backgroundColor:
            theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          backgroundColor:
            theme.palette.mode === 'dark' ? '#000000' : '#ffffff',
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
          p: 1,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
            fontWeight: 600,
          }}
        >
          Change Details
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {row && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Summary
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
            >
              {(() => {
                const action = String((row as any).action || '').toLowerCase();
                const color: 'success' | 'warning' | 'error' | 'default' =
                  action === 'insert'
                    ? 'success'
                    : action === 'update'
                    ? 'warning'
                    : action === 'delete'
                    ? 'error'
                    : 'default';
                return (
                  <Chip
                    label={formatTimestamp(
                      (row as any).timestamp ?? (row as any).timeStamp
                    )}
                    color={color}
                    size="small"
                  />
                );
              })()}
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <strong>
                {String((row as any).userName || (row as any).userId)}
              </strong>{' '}
              {getActionVerb((row as any).action)}{' '}
              <strong>{formatEntityName((row as any).entityName)}</strong>
            </Typography>
          </Box>
        </Box>
      )}
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Changed Fields
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <Accordion defaultExpanded={true} disableGutters>
            <AccordionDetails>
              <pre
                style={{
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No change details available.
                  </Typography>
                ) : (
                  items.map((item, idx) => {
                    const mapKey = String(item.field || '').replace(/\s+/g, '');
                    const displayName =
                      (fieldDisplayNames as Record<string, string>)[mapKey] ||
                      String(item.field || '');
                    const oldEmpty =
                      item.oldValue === '' ||
                      item.oldValue === null ||
                      item.oldValue === undefined ||
                      item.oldValue === 0 ||
                      item.oldValue === '0';
                    const newEmpty =
                      item.newValue === '' ||
                      item.newValue === null ||
                      item.newValue === undefined;
                    const oldText = oldEmpty ? 'empty' : String(item.oldValue);
                    const newText = newEmpty ? 'empty' : String(item.newValue);
                    return (
                      <Box key={idx} sx={{ mb: 1.25 }}>
                        <Typography variant="body2">
                          <strong>{displayName}</strong> was updated from{' '}
                          <Box>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                            >
                              <span
                                style={{
                                  textDecoration: 'line-through',
                                  color: '#d32f2f',
                                }}
                              >
                                {oldText}
                              </span>{' '}
                              <span
                                style={{
                                  fontSize: '24px',
                                  fontWeight: 'bold',
                                  verticalAlign: 'sub',
                                }}
                              >
                                →
                              </span>{' '}
                              <span style={{ color: '#2e7d32' }}>
                                {newText}
                              </span>
                            </Typography>
                          </Box>
                        </Typography>
                      </Box>
                    );
                  })
                )}
              </pre>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChangeDetailsDrawer;
